import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ArchiveBoxIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  ServerIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi, backupsApi } from '@/lib/api';
import { Backup } from '@/types';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function BackupsDashboard() {
  const queryClient = useQueryClient();
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState<Backup | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Backup | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<Backup | null>(null);
  const [newBackup, setNewBackup] = useState({
    name: '',
    description: '',
    format: 'custom' as string
  });

  // Fetch project details
  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch backups for the project
  const { data: backupsResponse, isLoading: backupsLoading } = useQuery(
    ['backups', projectId],
    () => backupsApi.getBackups(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
      refetchInterval: 5000, // Poll every 5s for in-progress backups
    }
  );

  // Create backup mutation
  const createBackupMutation = useMutation(
    (data: typeof newBackup) =>
      backupsApi.createBackup(projectId as string, {
        name: data.name,
        description: data.description || undefined,
        format: data.format
      }),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['backups', projectId]);
        setShowCreateModal(false);
        setNewBackup({ name: '', description: '', format: 'custom' });
        if (response.success) {
          toast.success('Backup created successfully');
        } else {
          toast.error('Backup creation failed');
        }
      },
      onError: () => {
        toast.error('Failed to create backup');
      },
    }
  );

  // Restore backup mutation
  const restoreBackupMutation = useMutation(
    (backupId: string) => backupsApi.restoreBackup(backupId),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['backups', projectId]);
        setShowRestoreConfirm(null);
        if (response.success) {
          toast.success('Database restored successfully');
        } else {
          toast.error('Restore failed');
        }
      },
      onError: () => {
        toast.error('Failed to restore backup');
      },
    }
  );

  // Delete backup mutation
  const deleteBackupMutation = useMutation(
    (backupId: string) => backupsApi.deleteBackup(backupId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['backups', projectId]);
        setShowDeleteConfirm(null);
        toast.success('Backup deleted');
      },
      onError: () => {
        toast.error('Failed to delete backup');
      },
    }
  );

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const project = projectResponse?.data?.project;
  const backupsData = backupsResponse?.data;
  const backups: Backup[] = backupsData?.backups || [];
  const stats = backupsData?.stats;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'restored': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'restoring': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return CheckCircleIcon;
      case 'restored': return CheckCircleIcon;
      case 'failed': return ExclamationTriangleIcon;
      case 'pending': return ClockIcon;
      case 'in_progress': return ArrowPathIcon;
      case 'restoring': return ArrowPathIcon;
      default: return ClockIcon;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_progress': return 'IN PROGRESS';
      default: return status.toUpperCase();
    }
  };

  return (
    <div className="px-4 sm:px-0">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Backups & Restore
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Create on-demand backups and restore your database for {project?.name || 'this project'}.
          </p>
        </div>
        {projectId && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Backup
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card">
            <div className="flex items-center">
              <ArchiveBoxIcon className="h-8 w-8 text-primary-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Backups</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.completed}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <ServerIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Size</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatBytes(stats.total_size_bytes)}
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Last Backup</p>
                <p className="text-lg font-semibold text-gray-900">
                  {stats.last_backup_at 
                    ? formatDistanceToNow(new Date(stats.last_backup_at), { addSuffix: true })
                    : 'Never'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backups List */}
      <div className="mt-8">
        {backupsLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : backups.length === 0 ? (
          <div className="text-center py-12 card">
            <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No backups yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Create your first backup to protect your data.
            </p>
            <div className="mt-4">
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create First Backup
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {backups.map((backup) => {
              const StatusIcon = getStatusIcon(backup.status);
              const isInProgress = backup.status === 'in_progress' || backup.status === 'restoring';

              return (
                <div key={backup.id} className="card">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <StatusIcon className={`h-5 w-5 mr-3 ${isInProgress ? 'animate-spin text-blue-500' : 'text-gray-400'}`} />
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">
                            {backup.name}
                          </h3>
                          {backup.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{backup.description}</p>
                          )}
                          <div className="flex items-center mt-1 space-x-4 flex-wrap">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(backup.status)}`}>
                              {getStatusLabel(backup.status)}
                            </span>
                            <span className="text-xs text-gray-500">
                              Format: {backup.format}
                            </span>
                            {backup.file_size_bytes && (
                              <span className="text-xs text-gray-500">
                                Size: {formatBytes(backup.file_size_bytes)}
                              </span>
                            )}
                            {backup.execution_time_ms && (
                              <span className="text-xs text-gray-500">
                                Duration: {(backup.execution_time_ms / 1000).toFixed(1)}s
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              Created {formatDistanceToNow(new Date(backup.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          {backup.error_message && (
                            <p className="mt-1 text-xs text-red-600">
                              {backup.error_message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => setShowDetailModal(backup)}
                        className="btn-secondary"
                        title="View details"
                      >
                        <DocumentTextIcon className="h-4 w-4 mr-1" />
                        Details
                      </button>

                      {backup.status === 'completed' && (
                        <button
                          onClick={() => setShowRestoreConfirm(backup)}
                          disabled={restoreBackupMutation.isLoading}
                          className="btn-secondary disabled:opacity-50"
                          title="Restore from this backup"
                        >
                          <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                          Restore
                        </button>
                      )}
                      
                      {!isInProgress && (
                        <button
                          onClick={() => setShowDeleteConfirm(backup)}
                          disabled={deleteBackupMutation.isLoading}
                          className="btn-secondary text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Delete backup"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Backup Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Backup</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Backup Name</label>
                  <input
                    type="text"
                    value={newBackup.name}
                    onChange={(e) => setNewBackup({ ...newBackup, name: e.target.value })}
                    className="mt-1 form-input w-full"
                    placeholder="e.g., pre-migration-backup"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
                  <textarea
                    value={newBackup.description}
                    onChange={(e) => setNewBackup({ ...newBackup, description: e.target.value })}
                    className="mt-1 form-textarea w-full"
                    rows={2}
                    placeholder="Brief description of why this backup was created"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Format</label>
                  <select
                    value={newBackup.format}
                    onChange={(e) => setNewBackup({ ...newBackup, format: e.target.value })}
                    className="mt-1 form-select w-full"
                  >
                    <option value="custom">Custom (compressed, recommended)</option>
                    <option value="plain">Plain SQL</option>
                    <option value="tar">Tar archive</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Custom format is recommended: it's compressed and supports parallel restore.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createBackupMutation.mutate(newBackup)}
                  disabled={createBackupMutation.isLoading || !newBackup.name}
                  className="btn-primary disabled:opacity-50"
                >
                  {createBackupMutation.isLoading ? 'Creating...' : 'Create Backup'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Confirm Restore</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Are you sure you want to restore from backup <strong>{showRestoreConfirm.name}</strong>?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Warning:</strong> This will drop all existing tables in the public schema and replace them with the backup data. This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowRestoreConfirm(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => restoreBackupMutation.mutate(showRestoreConfirm.id)}
                  disabled={restoreBackupMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50"
                >
                  {restoreBackupMutation.isLoading ? 'Restoring...' : 'Restore Database'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <TrashIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Delete Backup</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete backup <strong>{showDeleteConfirm.name}</strong>?
                This will permanently remove the backup file and cannot be undone.
              </p>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteBackupMutation.mutate(showDeleteConfirm.id)}
                  disabled={deleteBackupMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {deleteBackupMutation.isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Backup Details
                </h3>
                <button
                  onClick={() => setShowDetailModal(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  ✕
                </button>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Name</p>
                    <p className="text-sm text-gray-900">{showDetailModal.name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Status</p>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(showDetailModal.status)}`}>
                      {getStatusLabel(showDetailModal.status)}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Format</p>
                    <p className="text-sm text-gray-900">{showDetailModal.format}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Type</p>
                    <p className="text-sm text-gray-900">{showDetailModal.type}</p>
                  </div>
                  {showDetailModal.file_size_bytes && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Backup Size</p>
                      <p className="text-sm text-gray-900">{formatBytes(showDetailModal.file_size_bytes)}</p>
                    </div>
                  )}
                  {showDetailModal.database_size_bytes && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Database Size</p>
                      <p className="text-sm text-gray-900">{formatBytes(showDetailModal.database_size_bytes)}</p>
                    </div>
                  )}
                  {showDetailModal.execution_time_ms && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Duration</p>
                      <p className="text-sm text-gray-900">{(showDetailModal.execution_time_ms / 1000).toFixed(2)}s</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Created</p>
                    <p className="text-sm text-gray-900">
                      {new Date(showDetailModal.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                {showDetailModal.description && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Description</p>
                    <p className="text-sm text-gray-900 mt-1">{showDetailModal.description}</p>
                  </div>
                )}

                {showDetailModal.error_message && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-xs font-medium text-red-800 uppercase">Error</p>
                    <p className="text-sm text-red-700 mt-1">{showDetailModal.error_message}</p>
                  </div>
                )}

                {showDetailModal.metadata && Object.keys(showDetailModal.metadata).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2">Metadata</p>
                    <div className="bg-gray-50 rounded-md p-3">
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                        {JSON.stringify(showDetailModal.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDetailModal(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
